import { useSelfTimeCapsules, useSharedTimeCapsules } from "api/ApiHooks"
import { ButtonLink, Card, Spinner } from "components/ui"
import { UniqueGradient } from "components/UniqueGradient"
import { LockClosedIcon, LockOpenIcon, PlusIcon } from "@heroicons/react/24/outline"
import dayjs from "dayjs"
import { Link } from "react-router-dom"
import { formatCompactTimeDiff } from "utils/FormatTimeDiff"
import { TutorialPage } from "./TutorialPage"

type DashboardCapsule = {
  id: string
  name: string
  routePath: string
  opensAt?: string
  ownerName?: string
}

const isLocked = (capsule: DashboardCapsule) =>
  !!capsule.opensAt && dayjs(capsule.opensAt).isAfter(dayjs())

const CapsuleCard = ({ capsule }: { capsule: DashboardCapsule }) => {
  const locked = isLocked(capsule)
  const timing = !capsule.opensAt
    ? "Draft"
    : locked
      ? `Opens in ${formatCompactTimeDiff(capsule.opensAt)}`
      : `Opened ${formatCompactTimeDiff(capsule.opensAt)} ago`

  return (
    <Link
      to={`/time-capsules/${capsule.routePath}`}
      className="group rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <Card className="flex h-full items-center gap-x-4 group-hover:ring-primary-400 dark:group-hover:ring-primary-500">
        <UniqueGradient
          id={capsule.id}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg"
        >
          {locked ? (
            <LockClosedIcon aria-hidden="true" className="h-5 w-5 text-white" />
          ) : (
            <LockOpenIcon aria-hidden="true" className="h-5 w-5 text-white" />
          )}
        </UniqueGradient>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-primary-600 dark:text-gray-100 dark:group-hover:text-primary-400">
            {capsule.name}
          </p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {capsule.ownerName ? `by ${capsule.ownerName} · ${timing}` : timing}
          </p>
        </div>
      </Card>
    </Link>
  )
}

const Section = ({
  title,
  capsules
}: {
  title: string
  capsules: DashboardCapsule[]
}) => {
  if (capsules.length === 0) return null

  return (
    <section className="mt-10">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {title}
      </h3>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {capsules.map((capsule) => (
          <CapsuleCard key={capsule.id} capsule={capsule} />
        ))}
      </div>
    </section>
  )
}

export const DashboardPage = () => {
  const ownQuery = useSelfTimeCapsules()
  const sharedQuery = useSharedTimeCapsules()

  const isLoading = ownQuery.isLoading || sharedQuery.isLoading

  const owned: DashboardCapsule[] = (ownQuery.data?.timeCapsules ?? []).map(
    (capsule) => ({
      id: capsule.id,
      name: capsule.name,
      routePath: capsule.slug ?? "",
      opensAt: capsule.opensAt
    })
  )

  const shared: DashboardCapsule[] = (sharedQuery.data?.timeCapsules ?? []).map(
    (capsule) => ({
      id: capsule.id,
      name: capsule.name,
      routePath: `shared/${capsule.creatorId}/${capsule.slug}`,
      opensAt: capsule.opensAt,
      ownerName: capsule.creatorName
    })
  )

  const all = [...owned, ...shared]

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  // First timers (no capsules of any kind) get the "How It Works" walkthrough.
  if (all.length === 0) {
    return <TutorialPage />
  }

  // Unopened first, soonest to open at the top.
  const upcoming = all
    .filter(isLocked)
    .sort((a, b) => dayjs(a.opensAt).valueOf() - dayjs(b.opensAt).valueOf())
  // Opened, most recently opened first.
  const opened = all
    .filter((capsule) => !isLocked(capsule))
    .sort((a, b) => dayjs(b.opensAt ?? 0).valueOf() - dayjs(a.opensAt ?? 0).valueOf())

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
            Your Time Capsules
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {upcoming.length} waiting to open · {opened.length} opened
          </p>
        </div>
        <ButtonLink to="/new/time-capsule">
          <PlusIcon aria-hidden="true" className="-ml-0.5 mr-1.5 h-5 w-5" />
          New capsule
        </ButtonLink>
      </div>

      <Section title="Upcoming" capsules={upcoming} />
      <Section title="Opened" capsules={opened} />
    </div>
  )
}
